/* News_Details_UI - Componente de presentación para detalles de noticias */
import React from 'react';
import { Button } from "@material-tailwind/react";
import { ArrowLeft, Calendar, User, Tag, Facebook, Twitter, Linkedin, MessageCircle, Send } from "lucide-react";

const NewsDetailsUI = ({
  noticia,
  language,
  texts,
  comentario,
  comentarios,
  onVolverNoticias,
  onComentarioChange,
  onEnviarComentario,
  onCompartirFacebook,
  onCompartirTwitter,
  onCompartirLinkedIn
}) => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Navigation */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <Button 
            variant="text"
            onClick={onVolverNoticias} 
            className="flex items-center gap-2 text-green-600 hover:bg-green-50 normal-case font-semibold"
          >
            <ArrowLeft size={20} />
            {texts.backToNews}
          </Button>
        </div>
      </div>

      {/* Article Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <article className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-96 md:h-[500px] overflow-hidden">
            <img 
              src={noticia.image} 
              alt={noticia.title[language]} 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          </div>
          
          {/* Article Header */}
          <div className="p-8 md:p-12">
            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-6">
              <div className="flex items-center gap-2">
                <Calendar size={18} className="text-green-600" />
                <span className="text-sm font-medium">{noticia.date}</span>
              </div>
              
              {noticia.author && (
                <>
                  <span className="text-gray-300">•</span>
                  <div className="flex items-center gap-2">
                    <User size={18} className="text-green-600" />
                    <span className="text-sm font-medium">{noticia.author}</span>
                  </div>
                </>
              )}
              
              {noticia.category && (
                <>
                  <span className="text-gray-300">•</span>
                  <div className="flex items-center gap-2">
                    <Tag size={18} className="text-green-600" />
                    <span className="text-sm font-medium">{noticia.category}</span>
                  </div>
                </>
              )}
            </div>
            
            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {noticia.title[language]}
            </h1>
            
            {/* Divider */}
            <div className="w-20 h-1 bg-gradient-to-r from-green-600 to-teal-600 mb-8" />
            
            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 text-lg leading-relaxed whitespace-pre-line">
                {noticia.content[language]}
              </p>
            </div>
          </div>
          
          {/* Social Share Section */}
          <div className="px-8 md:px-12 pb-8">
            <div className="border-t border-gray-200 pt-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {language === 'es' ? 'Compartir artículo' : 'Share article'}
              </h3>
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={onCompartirFacebook}
                  className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2 normal-case"
                >
                  <Facebook size={20} />
                  Facebook
                </Button>
                <Button 
                  onClick={onCompartirTwitter}
                  className="bg-blue-500 hover:bg-blue-600 flex items-center gap-2 normal-case"
                >
                  <Twitter size={20} />
                  Twitter
                </Button>
                <Button 
                  onClick={onCompartirLinkedIn}
                  className="bg-blue-700 hover:bg-blue-800 flex items-center gap-2 normal-case"
                >
                  <Linkedin size={20} />
                  LinkedIn
                </Button>
              </div>
            </div>
          </div>
        </article>

        {/* Comments Section */}
        <div className="mt-8 bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <MessageCircle className="text-green-600" size={28} />
            <h3 className="text-2xl font-bold text-gray-900">
              {language === 'es' ? 'Comentarios' : 'Comments'}
            </h3>
            {comentarios.length > 0 && (
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                {comentarios.length}
              </span>
            )}
          </div>
          
          {/* Comment Form */}
          <div className="mb-8">
            <textarea 
              value={comentario}
              onChange={onComentarioChange}
              className="w-full h-32 p-4 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none transition-colors resize-none"
              placeholder={language === 'es' ? 'Escribe tu comentario...' : 'Write your comment...'}
            />
            <div className="flex justify-end mt-3">
              <Button 
                onClick={onEnviarComentario}
                disabled={!comentario.trim()}
                className="bg-green-600 hover:bg-green-700 flex items-center gap-2 normal-case"
              >
                <Send size={18} />
                {language === 'es' ? 'Enviar comentario' : 'Send comment'}
              </Button>
            </div>
          </div>
          
          {/* Comments List */}
          {comentarios.length > 0 ? (
            <div className="space-y-4">
              {comentarios.map((com) => (
                <div key={com.id} className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">
                      {language === 'es' ? 'Usuario Anónimo' : 'Anonymous User'}
                    </span>
                    <span className="text-sm text-gray-500">{com.fecha}</span>
                  </div>
                  <p className="text-gray-700">{com.texto}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              {language === 'es' 
                ? 'Sé el primero en comentar' 
                : 'Be the first to comment'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsDetailsUI;